import React from "react";
import {
  Home,
  Car,
  Gem,
  Palette,
  CircleDollarSign,
  BarChart3,
  Bitcoin,
  CircleDot,
  Building2,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { AlertTriangle } from "lucide-react";

import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { validateWillExtraction, getPropertyScope } from "@/shared/utils/willValidation";
import { useCitation, getCitationForField } from "./CitationContext";

export interface WillExtraction {
  document_background?: {
    date_signed?: string | null;
    date_signed_raw?: string | null;
    state?: string | null;
    location_signed?: { city?: string | null; county?: string | null; state?: string | null } | null;
    is_self_proving?: boolean;
    self_proving_timing?: "at_execution" | "later" | null;
    revokes_prior_wills?: boolean;
    testator_name_as_signed?: string | null;
    also_known_as?: string[] | null;
    drafting_attorney?: { name?: string | null; firm?: string | null } | null;
    page_count?: number | null;
    page_continuity_flag?: boolean | null;
    earlier_instruments?: Array<{ type?: string; description?: string; date?: string }> | null;
  };
  signing_and_witnesses?: {
    attestation_clause_present?: boolean;
    signature_at_end?: boolean;
    text_after_signature?: boolean;
    witness_count?: number | null;
    visible_alterations?: Array<{ description?: string; page_reference?: string }> | null;
  } | null;
  executors?: Array<{
    name: string;
    type?: "individual" | "corporate";
    role?: "executor" | "co-executor";
    is_primary?: boolean;
    order?: number;
    relationship?: string | null;
    co_executor_terms?: "joint" | "independent" | "majority" | null;
    bond_waiver?: "waived" | "required" | "silent" | null;
    power_to_sell_real_property?: boolean | null;
  }>;
  key_people?: Array<{
    name: string;
    roles?: string[];
    relationship?: string | null;
  }>;
  specific_bequests?: Array<{
    item_description: string;
    estimated_value?: number | null;
    category?: string | null;
    beneficiaries?: Array<{
      name: string;
      percentage?: number | null;
    }>;
    contingent_beneficiaries?: Array<{
      name: string;
      percentage?: number | null;
    }>;
  }>;
  residuary_estate?: {
    distribution_scheme?: "per_stirpes" | "per_capita" | "per_capita_at_each_generation" | "other" | null;
    survivorship_period?: string | null;
    beneficiaries?: Array<{
      name: string;
      percentage?: number | null;
      contingency?: string | null;
      contingent_beneficiaries?: Array<{
        name: string;
        percentage?: number | null;
      }>;
    }>;
  };
  charitable_contributions?: Array<{
    organization_name: string;
    organization_type?: "religious" | "educational" | "medical" | "civic" | "other" | null;
    description?: string | null;
    amount_type?: "dollar" | "percentage";
    amount?: number;
  }>;
  testamentary_trusts?: Array<{
    trust_name?: string | null;
    beneficiaries?: Array<{ name: string; relationship?: string }>;
    trustees?: Array<{ name: string; role?: string; order?: number }>;
    funding_source?: string | null;
    distribution_conditions?: string | null;
  }> | null;
  power_of_appointment?: {
    has_power?: boolean;
    type?: "general" | "limited" | "special" | null;
    holder?: string | null;
    description?: string | null;
  } | null;
  trust_references?: Array<{
    trust_name?: string;
    trust_date?: string;
    relationship?: "pour_over" | "beneficiary" | "referenced";
    is_pour_over?: boolean;
  }>;
  instructions_and_clauses?: {
    funeral_instructions?: {
      has_instructions?: boolean;
      type?: "burial" | "cremation" | "other";
      details?: string;
    } | null;
    debt_payment_direction?: string | null;
    tax_apportionment_clause?: { has_clause?: boolean; description?: string } | null;
    personal_property_memorandum_referenced?: boolean | null;
  } | null;
  guardianship?: {
    has_minor_children?: boolean;
    minor_children_names?: string[];
    primary_guardian?: { name: string; relationship?: string | null } | null;
    secondary_guardian?: { name: string; relationship?: string | null } | null;
  } | null;
  digital_assets?: {
    has_clause?: boolean;
    instructions?: string | null;
    digital_fiduciary?: string | null;
  } | null;
  no_contest_clause?: {
    has_clause?: boolean;
    clause_text?: string | null;
  } | null;
  witness_info?: Array<{
    name: string;
    address?: string | null;
  }>;
  disinheritance?: Array<{ name?: string; relationship?: string; reason_stated?: string }>;
  financial_institutions?: Array<{ name?: string; type?: string; account_numbers?: string[] }>;
  real_property?: Array<{ address?: string; property_type?: string; description?: string }>;
}

const CATEGORY_CONFIG: Record<
  string,
  {
    bg: string;
    text: string;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
  }
> = {
  real_estate: {
    bg: "bg-green-100",
    text: "text-green-700",
    icon: Home,
    label: "Property",
  },
  vehicle: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: Car,
    label: "Vehicle",
  },
  jewelry: {
    bg: "bg-pink-100",
    text: "text-pink-700",
    icon: Gem,
    label: "Jewelry",
  },
  art: { bg: "bg-red-100", text: "text-red-700", icon: Palette, label: "Art" },
  cash: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    icon: CircleDollarSign,
    label: "Cash",
  },
  stocks: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    icon: BarChart3,
    label: "Stock",
  },
  crypto: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    icon: Bitcoin,
    label: "Crypto",
  },
  life_insurance_policy: {
    bg: "bg-teal-100",
    text: "text-teal-700",
    icon: CircleDot,
    label: "Life Insurance Policy",
  },
  other: {
    bg: "bg-subtle",
    text: "text-stone-600",
    icon: Building2,
    label: "Other",
  },
};

function CategoryBadge({ category }: { category: string | null | undefined }) {
  if (!category) return null;
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm whitespace-nowrap flex-shrink-0 ${config.bg} ${config.text}`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {config.label}
    </span>
  );
}

function formatRoleName(role: string | null | undefined): string {
  if (!role) return "";
  return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function formatLocationSigned(
  locationSigned: { city?: string | null; county?: string | null; state?: string | null } | null | undefined,
  fallbackState: string | null | undefined,
): string | null {
  if (locationSigned) {
    const parts = [locationSigned.city, locationSigned.county, locationSigned.state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  }
  return fallbackState || null;
}

function ValidationWarnings({ warnings }: { warnings: { message: string; severity: "warning" | "error" }[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-2 px-4 py-3">
      {warnings.map((w, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 text-sm rounded-md p-2 ${
            w.severity === "error"
              ? "bg-red-50 text-red-700"
              : "bg-yellow-50 text-yellow-700"
          }`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{w.message}</span>
        </div>
      ))}
    </div>
  );
}

interface ExtractedContact {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  relationship?: string;
  legal_role?: string[];
  contact_info?: {
    email?: string;
    phone?: string;
    address?: string;
  };
}

export default function WillInsights({
  willData,
  contacts,
  citations,
}: {
  willData: WillExtraction;
  contacts?: ExtractedContact[];
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const findCitation = (...paths: string[]): string | null => {
    for (const path of paths) {
      if (getCitationForField(citations, path)) return path;
    }
    return null;
  };

  const {
    document_background,
    signing_and_witnesses,
    executors: willExecutors,
    specific_bequests,
    residuary_estate,
    charitable_contributions,
    testamentary_trusts,
    power_of_appointment,
    trust_references,
    instructions_and_clauses,
    guardianship,
    digital_assets,
    no_contest_clause,
    witness_info,
    financial_institutions,
    real_property,
  } = willData;

  const deceasedUserData = useDeceasedUserData();
  const validationWarnings = React.useMemo(
    () =>
      validateWillExtraction(willData, deceasedUserData.hasPersonData
        ? { fullName: deceasedUserData.fullName, dateOfBirth: deceasedUserData.dateOfBirth, dateOfDeath: deceasedUserData.dateOfDeath }
        : null,
      ),
    [willData, deceasedUserData.fullName, deceasedUserData.dateOfBirth, deceasedUserData.dateOfDeath, deceasedUserData.hasPersonData],
  );
  const propertyScope = React.useMemo(() => getPropertyScope(willData), [willData]);

  const warningsForSection = (section: string) =>
    validationWarnings.filter((w) => w.section === section);

  const isValidName = (name: string | undefined) =>
    name && !name.toLowerCase().includes("unknown") && name !== "N/A";

  const validContacts = contacts?.filter((c) => isValidName(c.full_name)) || [];

  const executorContacts = validContacts.filter((c) =>
    c.legal_role?.some((role) => ["executor", "co-executor"].includes(role.toLowerCase())),
  );

  const isPrimary = (e: { is_primary?: boolean; order?: number }) => e.is_primary === true || e.order === 1;

  const willPrimaryExecutor = willExecutors?.find(isPrimary);
  const willSecondaryExecutor =
    willExecutors?.find((e) => !isPrimary(e) && e.order === 2) ||
    willExecutors?.find((e) => !isPrimary(e) && e !== willPrimaryExecutor);

  const primaryExecutor =
    executorContacts[0] ||
    (willPrimaryExecutor
      ? {
          full_name: willPrimaryExecutor.name,
          relationship: willPrimaryExecutor.relationship,
        }
      : undefined);
  const secondaryExecutor =
    (executorContacts.length > 1 ? executorContacts[1] : undefined) ||
    (willSecondaryExecutor
      ? {
          full_name: willSecondaryExecutor.name,
          relationship: willSecondaryExecutor.relationship,
        }
      : undefined);

  const keyPeople = validContacts.filter(
    (c) =>
      c.legal_role &&
      c.legal_role.length > 0 &&
      !c.legal_role.some((role) => ["witness", "notary"].includes(role)),
  );

  const bg = document_background;
  const locationState = bg?.location_signed?.state ?? bg?.state ?? null;
  const locationDisplay = formatLocationSigned(bg?.location_signed, locationState);

  const selfProvingLabel =
    bg?.self_proving_timing === "at_execution"
      ? "Yes (at execution)"
      : bg?.self_proving_timing === "later"
      ? "Yes (later)"
      : bg?.is_self_proving
      ? "Yes"
      : "No";

  const hasBgSection = !!(
    bg?.date_signed ||
    bg?.testator_name_as_signed ||
    (bg?.also_known_as && bg.also_known_as.length > 0) ||
    locationDisplay ||
    bg?.is_self_proving != null ||
    bg?.revokes_prior_wills != null ||
    bg?.drafting_attorney?.name ||
    bg?.page_count ||
    bg?.page_continuity_flag === false ||
    (bg?.earlier_instruments && bg.earlier_instruments.length > 0)
  );

  const hasSigningSection = !!(
    signing_and_witnesses?.attestation_clause_present != null ||
    signing_and_witnesses?.signature_at_end != null ||
    signing_and_witnesses?.text_after_signature ||
    signing_and_witnesses?.witness_count != null ||
    (signing_and_witnesses?.visible_alterations && signing_and_witnesses.visible_alterations.length > 0) ||
    (Array.isArray(witness_info) && witness_info.length > 0) ||
    warningsForSection("signing_and_witnesses").length > 0
  );

  return (
    <div className="px-6 pb-6 space-y-4">
      {hasBgSection && (
        <InsightsCard title="Document background" noPadding>
          <ValidationWarnings warnings={warningsForSection("document_background")} />
          <InsightsDividerList>
            {bg?.date_signed && (
              <InsightsDividerRow
                label="Date signed"
                value={formatDate(bg.date_signed)}
                onClick={
                  getCitationForField(citations, "document_background.date_signed")
                    ? () => handleFieldClick("document_background.date_signed")
                    : undefined
                }
              />
            )}
            {bg?.testator_name_as_signed && (
              <InsightsDividerRow
                label="Name as signed"
                value={bg.testator_name_as_signed}
                onClick={
                  getCitationForField(citations, "document_background.testator_name_as_signed")
                    ? () => handleFieldClick("document_background.testator_name_as_signed")
                    : undefined
                }
              />
            )}
            {bg?.also_known_as && bg.also_known_as.length > 0 && (
              <InsightsDividerRow
                label="Also known as"
                value={bg.also_known_as.join(", ")}
                onClick={
                  getCitationForField(citations, "document_background.also_known_as")
                    ? () => handleFieldClick("document_background.also_known_as")
                    : undefined
                }
              />
            )}
            {locationDisplay && (
              <InsightsDividerRow
                label="Location signed"
                value={locationDisplay}
                onClick={(() => {
                  const path = findCitation(
                    "document_background.location_signed.state",
                    "document_background.location_signed.city",
                    "document_background.location_signed.county",
                    "document_background.state",
                  );
                  return path ? () => handleFieldClick(path) : undefined;
                })()}
              />
            )}
            {bg?.is_self_proving != null && (
              <InsightsDividerRow
                label="Self-proving"
                value={selfProvingLabel}
                onClick={
                  getCitationForField(citations, "document_background.is_self_proving")
                    ? () => handleFieldClick("document_background.is_self_proving")
                    : undefined
                }
              />
            )}
            {bg?.revokes_prior_wills != null && (
              <InsightsDividerRow
                label="Revokes prior wills"
                value={bg.revokes_prior_wills ? "Yes" : "No"}
                onClick={
                  getCitationForField(citations, "document_background.revokes_prior_wills")
                    ? () => handleFieldClick("document_background.revokes_prior_wills")
                    : undefined
                }
              />
            )}
            {(bg?.drafting_attorney?.name || bg?.drafting_attorney?.firm) && (
              <InsightsDividerRow
                label="Drafting attorney"
                value={
                  [bg.drafting_attorney?.name, bg.drafting_attorney?.firm]
                    .filter(Boolean)
                    .join(", ") || ""
                }
                onClick={(() => {
                  const path = findCitation(
                    "document_background.drafting_attorney.name",
                    "document_background.drafting_attorney.firm",
                    "document_background.drafting_attorney",
                  );
                  return path ? () => handleFieldClick(path) : undefined;
                })()}
              />
            )}
            {bg?.page_count != null && (
              <InsightsDividerRow
                label="Page count"
                value={String(bg.page_count)}
                onClick={
                  getCitationForField(citations, "document_background.page_count")
                    ? () => handleFieldClick("document_background.page_count")
                    : undefined
                }
              />
            )}
            {bg?.page_continuity_flag === false && (
              <InsightsDividerRow
                label="Page continuity"
                value="Pages may be missing or out of order"
                onClick={
                  getCitationForField(citations, "document_background.page_continuity_flag")
                    ? () => handleFieldClick("document_background.page_continuity_flag")
                    : undefined
                }
              />
            )}
            {bg?.earlier_instruments && bg.earlier_instruments.length > 0 && (
              <div className="py-2.5 px-4">
                <span className="text-sm text-secondary block mb-1">Earlier instruments</span>
                <div className="space-y-1">
                  {bg.earlier_instruments.map((instrument, i) => (
                    <p key={i} className="text-sm text-primary">
                      {[instrument.type, instrument.description, instrument.date ? formatDate(instrument.date) : null]
                        .filter(Boolean)
                        .join(" - ")}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {propertyScope && (
              <InsightsDividerRow label="Property scope" value={propertyScope} />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {Array.isArray(willExecutors) && willExecutors.length > 0 && (
        <InsightsCard title="Executor" noPadding>
          <div className="divide-y divide-stone-100">
            {willExecutors.map((executor, idx) => {
              const contact = idx === 0 ? primaryExecutor : idx === 1 ? secondaryExecutor : undefined;
              const name = contact?.full_name || executor.name;
              const relationship = contact?.relationship || executor.relationship;
              const label = isPrimary(executor) ? "Primary executor" : executor.role === "co-executor" ? "Co-executor" : "Successor executor";

              return (
                <div key={idx} className="py-3 px-4 space-y-2">
                  <div>
                    <p className="text-sm text-stone-500">{label}</p>
                    <p className="text-sm font-medium text-stone-900">
                      {name}
                      {relationship && (
                        <span className="font-normal text-stone-500"> ({formatRoleName(relationship)})</span>
                      )}
                    </p>
                  </div>
                  {executor.co_executor_terms && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Co-executor terms</span>
                      <span className="text-stone-900">{formatRoleName(executor.co_executor_terms)}</span>
                    </div>
                  )}
                  {executor.bond_waiver && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Bond</span>
                      <span className="text-stone-900">{formatRoleName(executor.bond_waiver)}</span>
                    </div>
                  )}
                  {executor.power_to_sell_real_property != null && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Power to sell real property</span>
                      <span className="text-stone-900">{executor.power_to_sell_real_property ? "Yes" : "No"}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </InsightsCard>
      )}

      {keyPeople && keyPeople.length > 0 && (
        <InsightsCard title="Key people" noPadding>
          <InsightsDividerList>
            {keyPeople.slice(0, 5).map((person, index) => (
              <InsightsDividerRow
                key={index}
                label={person.full_name || ""}
                labelClassName="text-primary font-medium"
                layout="between"
                value={[
                  ...(person.legal_role || []).map(formatRoleName),
                  formatRoleName(person.relationship),
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            ))}
          </InsightsDividerList>
          {keyPeople.length > 0 && (
            <div className="py-2.5 px-4 border-t border-stone-100">
              <Link
                to="/contacts"
                className="text-sm text-stone-600 flex items-center gap-1 hover:underline"
              >
                <span>View all in People</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </InsightsCard>
      )}

      {Array.isArray(specific_bequests) && specific_bequests.length > 0 && (
        <InsightsCard title="Specific bequests (gifts)" noPadding>
          <div className="divide-y divide-stone-100">
            {specific_bequests.map((bequest, index) => (
              <div key={index} className="py-3 px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900">
                      {bequest.item_description}
                      {bequest.estimated_value && (
                        <span className="font-normal text-stone-500">
                          {" "}
                          ({formatCurrency(bequest.estimated_value)})
                        </span>
                      )}
                    </p>
                    {bequest.beneficiaries && bequest.beneficiaries.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {bequest.beneficiaries.map((beneficiary, bIndex) => (
                          <p key={bIndex} className="text-sm text-stone-500">
                            {bequest.beneficiaries!.length === 1
                              ? beneficiary.name
                              : beneficiary.percentage != null
                              ? `${beneficiary.name} (${beneficiary.percentage}%)`
                              : beneficiary.name}
                          </p>
                        ))}
                      </div>
                    )}
                    {bequest.contingent_beneficiaries && bequest.contingent_beneficiaries.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        <p className="text-xs text-stone-400 uppercase tracking-wide">If primary unavailable</p>
                        {bequest.contingent_beneficiaries.map((cb, cbIndex) => (
                          <p key={cbIndex} className="text-sm text-stone-500">
                            {cb.name}
                            {cb.percentage != null && ` (${cb.percentage}%)`}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <CategoryBadge category={bequest.category} />
                </div>
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {Array.isArray(residuary_estate?.beneficiaries) &&
        residuary_estate!.beneficiaries!.length > 0 && (
          <InsightsCard title="Residual contributions" noPadding>
            <InsightsDividerList>
              {residuary_estate!.distribution_scheme && (
                <InsightsDividerRow
                  label="Distribution scheme"
                  value={formatRoleName(residuary_estate!.distribution_scheme)}
                />
              )}
              {residuary_estate!.survivorship_period && (
                <InsightsDividerRow
                  label="Survivorship period"
                  value={residuary_estate!.survivorship_period}
                />
              )}
            </InsightsDividerList>
            <div className="divide-y divide-stone-100">
              {residuary_estate!.beneficiaries!.map((beneficiary, index) => (
                <div key={index} className="py-3 px-4 space-y-1">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-stone-900">{beneficiary.name}</span>
                    <span className="text-sm text-stone-900">
                      {beneficiary.percentage != null ? `${beneficiary.percentage}%` : ""}
                    </span>
                  </div>
                  {beneficiary.contingency && (
                    <p className="text-sm text-stone-400 italic">{beneficiary.contingency}</p>
                  )}
                  {beneficiary.contingent_beneficiaries?.map((cb, cbIndex) => (
                    <div key={cbIndex} className="flex justify-between items-start">
                      <span className="text-sm text-stone-900">{cb.name}</span>
                      {cb.percentage != null && (
                        <span className="text-sm text-stone-900">{cb.percentage}%</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </InsightsCard>
        )}

      {Array.isArray(charitable_contributions) && charitable_contributions.length > 0 && (
        <InsightsCard title="Charitable contributions" noPadding>
          <div className="divide-y divide-stone-100">
            {charitable_contributions.map((contribution, index) => (
              <div key={index} className="flex justify-between items-start py-3 px-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-stone-900">
                      {contribution.organization_name}
                    </p>
                    {contribution.organization_type && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-stone-100 text-stone-600">
                        {formatRoleName(contribution.organization_type)}
                      </span>
                    )}
                  </div>
                  {contribution.description && (
                    <p className="text-sm text-stone-500 mt-0.5">{contribution.description}</p>
                  )}
                </div>
                <span className="text-sm text-stone-900">
                  {contribution.amount_type === "dollar"
                    ? formatCurrency(contribution.amount)
                    : `${contribution.amount}%`}
                </span>
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {guardianship && guardianship.has_minor_children && (
        <InsightsCard title="Guardianship for minor children" noPadding>
          <div className="divide-y divide-stone-100">
            {guardianship.primary_guardian && (
              <div
                className={`py-3 px-4${getCitationForField(citations, "guardianship.primary_guardian.name") ? " cursor-pointer hover:bg-subtle transition-colors rounded-sm" : ""}`}
                onClick={
                  getCitationForField(citations, "guardianship.primary_guardian.name")
                    ? () => handleFieldClick("guardianship.primary_guardian.name")
                    : undefined
                }
              >
                <p className="text-sm text-stone-500 inline-flex items-center">
                  Primary guardian
                  {getCitationForField(citations, "guardianship.primary_guardian.name") && (
                    <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                  )}
                </p>
                <p className="text-sm text-stone-900 mt-0.5">
                  {guardianship.primary_guardian.name}
                </p>
              </div>
            )}
            {guardianship.secondary_guardian && (
              <div
                className={`py-3 px-4${getCitationForField(citations, "guardianship.secondary_guardian.name") ? " cursor-pointer hover:bg-subtle transition-colors rounded-sm" : ""}`}
                onClick={
                  getCitationForField(citations, "guardianship.secondary_guardian.name")
                    ? () => handleFieldClick("guardianship.secondary_guardian.name")
                    : undefined
                }
              >
                <p className="text-sm text-stone-500 inline-flex items-center">
                  Secondary guardian
                  {getCitationForField(citations, "guardianship.secondary_guardian.name") && (
                    <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                  )}
                </p>
                <p className="text-sm text-stone-900 mt-0.5">
                  {guardianship.secondary_guardian.name}
                </p>
              </div>
            )}
            {Array.isArray(guardianship.minor_children_names) &&
              guardianship.minor_children_names.length > 0 && (
                <div className="py-3 px-4">
                  <p className="text-sm text-stone-500">Minor children</p>
                  <p className="text-sm text-stone-900 mt-0.5">
                    {guardianship.minor_children_names.join(", ")}
                  </p>
                </div>
              )}
          </div>
        </InsightsCard>
      )}

      {no_contest_clause && no_contest_clause.has_clause && (
        <InsightsCard title="No-contest clause" noPadding>
          <InsightsDividerList>
            <InsightsDividerRow
              label="Has clause"
              value="Yes"
              onClick={
                getCitationForField(citations, "no_contest_clause.has_clause")
                  ? () => handleFieldClick("no_contest_clause.has_clause")
                  : undefined
              }
            />
            {no_contest_clause.clause_text && (
              <div
                className={`py-3 px-4${getCitationForField(citations, "no_contest_clause.clause_text") ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                onClick={
                  getCitationForField(citations, "no_contest_clause.clause_text")
                    ? () => handleFieldClick("no_contest_clause.clause_text")
                    : undefined
                }
              >
                <p className="text-sm text-stone-500 inline-flex items-center">
                  Clause text
                  {getCitationForField(citations, "no_contest_clause.clause_text") && (
                    <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                  )}
                </p>
                <p className="text-sm text-stone-900 mt-0.5">{no_contest_clause.clause_text}</p>
              </div>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {digital_assets && digital_assets.has_clause && (
        <InsightsCard title="Digital assets clause" noPadding>
          <InsightsDividerList>
            <InsightsDividerRow
              label="Has clause"
              value="Yes"
              onClick={
                getCitationForField(citations, "digital_assets.has_clause")
                  ? () => handleFieldClick("digital_assets.has_clause")
                  : undefined
              }
            />
            {digital_assets.digital_fiduciary && (
              <InsightsDividerRow
                label="Digital fiduciary"
                value={digital_assets.digital_fiduciary}
                onClick={
                  getCitationForField(citations, "digital_assets.digital_fiduciary")
                    ? () => handleFieldClick("digital_assets.digital_fiduciary")
                    : undefined
                }
              />
            )}
            {digital_assets.instructions && (
              <div
                className={`py-3 px-4${getCitationForField(citations, "digital_assets.instructions") ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                onClick={
                  getCitationForField(citations, "digital_assets.instructions")
                    ? () => handleFieldClick("digital_assets.instructions")
                    : undefined
                }
              >
                <p className="text-sm text-stone-500 inline-flex items-center">
                  Instructions
                  {getCitationForField(citations, "digital_assets.instructions") && (
                    <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                  )}
                </p>
                <p className="text-sm text-stone-900 mt-0.5">{digital_assets.instructions}</p>
              </div>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasSigningSection && (
        <InsightsCard title="Signing and witnesses" noPadding>
          <ValidationWarnings warnings={warningsForSection("signing_and_witnesses")} />
          <InsightsDividerList>
            {signing_and_witnesses?.attestation_clause_present != null && (
              <InsightsDividerRow
                label="Attestation clause"
                value={signing_and_witnesses.attestation_clause_present ? "Present" : "Not present"}
              />
            )}
            {signing_and_witnesses?.signature_at_end != null && (
              <InsightsDividerRow
                label="Signed at end"
                value={signing_and_witnesses.signature_at_end ? "Yes" : "No"}
              />
            )}
            {signing_and_witnesses?.text_after_signature && (
              <InsightsDividerRow
                label="Text after signature"
                value="Text found after signature - review advised"
              />
            )}
            {signing_and_witnesses?.witness_count != null && (
              <InsightsDividerRow
                label="Witness count"
                value={String(signing_and_witnesses.witness_count)}
              />
            )}
            {Array.isArray(witness_info) && witness_info.length > 0 && (
              <div className="py-2.5 px-4">
                <span className="text-sm text-secondary block mb-1">Witnesses</span>
                <div className="space-y-1">
                  {witness_info.map((witness, index) => (
                    <div key={`${witness.name}-${index}`} className="flex justify-between items-start">
                      <span className="text-sm text-primary font-medium">{witness.name}</span>
                      {witness.address && (
                        <span className="text-sm text-secondary text-right">{witness.address}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {signing_and_witnesses?.visible_alterations && signing_and_witnesses.visible_alterations.length > 0 && (
              <div className="py-2.5 px-4">
                <span className="text-sm text-secondary block mb-1">Visible alterations</span>
                <div className="space-y-1">
                  {signing_and_witnesses.visible_alterations.map((alt, i) => (
                    <p key={i} className="text-sm text-primary">
                      {alt.description}
                      {alt.page_reference && (
                        <span className="text-secondary"> (p. {alt.page_reference})</span>
                      )}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {Array.isArray(testamentary_trusts) && testamentary_trusts.length > 0 && (
        <InsightsCard title="Testamentary trusts" noPadding>
          <div className="divide-y divide-stone-100">
            {testamentary_trusts.map((trust, index) => (
              <div key={index} className="py-3 px-4 space-y-2">
                {trust.trust_name && (
                  <p className="text-sm font-medium text-stone-900">{trust.trust_name}</p>
                )}
                {trust.beneficiaries && trust.beneficiaries.length > 0 && (
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wide mb-0.5">Beneficiaries</p>
                    {trust.beneficiaries.map((b, bIndex) => (
                      <p key={bIndex} className="text-sm text-stone-600">
                        {b.name}
                        {b.relationship && ` (${formatRoleName(b.relationship)})`}
                      </p>
                    ))}
                  </div>
                )}
                {trust.trustees && trust.trustees.length > 0 && (
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wide mb-0.5">Trustees</p>
                    {trust.trustees.map((t, tIndex) => (
                      <p key={tIndex} className="text-sm text-stone-600">
                        {t.name}
                        {t.role && ` (${formatRoleName(t.role)})`}
                      </p>
                    ))}
                  </div>
                )}
                {trust.funding_source && (
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wide mb-0.5">Funding source</p>
                    <p className="text-sm text-stone-600">{trust.funding_source}</p>
                  </div>
                )}
                {trust.distribution_conditions && (
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wide mb-0.5">Distribution conditions</p>
                    <p className="text-sm text-stone-600">{trust.distribution_conditions}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {power_of_appointment?.has_power && (
        <InsightsCard title="Power of appointment" noPadding>
          <InsightsDividerList>
            <InsightsDividerRow label="Has power" value="Yes" />
            {power_of_appointment.type && (
              <InsightsDividerRow
                label="Type"
                value={formatRoleName(power_of_appointment.type)}
              />
            )}
            {power_of_appointment.holder && (
              <InsightsDividerRow label="Holder" value={power_of_appointment.holder} />
            )}
            {power_of_appointment.description && (
              <div className="py-2.5 px-4">
                <span className="text-sm text-secondary block mb-1">Description</span>
                <p className="text-sm text-primary">{power_of_appointment.description}</p>
              </div>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {Array.isArray(trust_references) && trust_references.length > 0 && (
        <InsightsCard title="Trust references" noPadding>
          <div className="divide-y divide-stone-100">
            {trust_references.map((ref, index) => (
              <div key={index} className="py-3 px-4 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-stone-900">
                    {ref.trust_name || "Unnamed trust"}
                  </span>
                  {ref.is_pour_over && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                      Pour-over
                    </span>
                  )}
                </div>
                {ref.trust_date && (
                  <p className="text-sm text-stone-500">{formatDate(ref.trust_date)}</p>
                )}
                {ref.relationship && !ref.is_pour_over && (
                  <p className="text-sm text-stone-500">{formatRoleName(ref.relationship)}</p>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {instructions_and_clauses && (
        (() => {
          const ic = instructions_and_clauses;
          const hasFuneral = ic.funeral_instructions?.has_instructions;
          const hasDebt = !!ic.debt_payment_direction;
          const hasTax = ic.tax_apportionment_clause?.has_clause;
          const hasMemoField = ic.personal_property_memorandum_referenced != null;

          if (!hasFuneral && !hasDebt && !hasTax && !hasMemoField) return null;

          return (
            <InsightsCard title="Instructions and clauses" noPadding>
              <InsightsDividerList>
                {hasFuneral && (
                  <>
                    <InsightsDividerRow
                      label="Funeral instructions"
                      value={ic.funeral_instructions?.type ? formatRoleName(ic.funeral_instructions.type) : "Yes"}
                    />
                    {ic.funeral_instructions?.details && (
                      <div className="py-2.5 px-4">
                        <span className="text-sm text-secondary block mb-1">Funeral details</span>
                        <p className="text-sm text-primary">{ic.funeral_instructions.details}</p>
                      </div>
                    )}
                  </>
                )}
                {hasDebt && (
                  <InsightsDividerRow
                    label="Debt payment direction"
                    value={ic.debt_payment_direction!}
                  />
                )}
                {hasTax && (
                  <>
                    <InsightsDividerRow label="Tax apportionment clause" value="Yes" />
                    {ic.tax_apportionment_clause?.description && (
                      <div className="py-2.5 px-4">
                        <span className="text-sm text-secondary block mb-1">Tax apportionment details</span>
                        <p className="text-sm text-primary">{ic.tax_apportionment_clause.description}</p>
                      </div>
                    )}
                  </>
                )}
                {hasMemoField && (
                  <InsightsDividerRow
                    label="Personal property memorandum"
                    value={ic.personal_property_memorandum_referenced ? "Referenced" : "Not referenced"}
                  />
                )}
              </InsightsDividerList>
            </InsightsCard>
          );
        })()
      )}

      {Array.isArray(real_property) && real_property.length > 0 && (
        <InsightsCard title="Real property" noPadding>
          <div className="divide-y divide-stone-100">
            {real_property.map((property, index) => (
              <div key={index} className="py-3 px-4 space-y-0.5">
                {property.address && (
                  <p className="text-sm font-medium text-stone-900">{property.address}</p>
                )}
                {property.property_type && (
                  <p className="text-sm text-stone-500">{formatRoleName(property.property_type)}</p>
                )}
                {property.description && (
                  <p className="text-sm text-stone-500">{property.description}</p>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {Array.isArray(financial_institutions) && financial_institutions.length > 0 && (
        <InsightsCard title="Financial institutions" noPadding>
          <div className="divide-y divide-stone-100">
            {financial_institutions.map((institution, index) => (
              <div key={index} className="py-3 px-4 space-y-0.5">
                {institution.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-stone-900">{institution.name}</span>
                    {institution.type && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-stone-100 text-stone-600">
                        {formatRoleName(institution.type)}
                      </span>
                    )}
                  </div>
                )}
                {institution.account_numbers && institution.account_numbers.length > 0 && (
                  <p className="text-sm text-stone-500">
                    {institution.account_numbers.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}
    </div>
  );
}
